import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with relative time plugin
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);

/**
 * Converts an ISO timestamp to a relative time string (e.g. "2h ago")
 * @param timestamp ISO timestamp string
 * @param withoutSuffix Optional boolean to show the relative time without the suffix (e.g. "2h")
 * @returns Relative time string
 */
export function formatTimeAgo(timestamp: string, withoutSuffix?: boolean): string {
  return dayjs(timestamp).fromNow(withoutSuffix);
}

/**
 * Checks if a timestamp is within the last 24 hours
 * @param timestamp ISO timestamp string
 * @returns boolean
 */
export function isWithin24Hours(timestamp: string): boolean {
  const now = dayjs();
  const time = dayjs(timestamp);
  return now.diff(time, 'hour') < 24;
}

/**
 * Formats a timestamp based on how recent it is
 * If within 24 hours: shows relative time (e.g. "2h ago") unless useTimeAgo is false
 * If older: shows date (e.g. "Dec 15, 2024")
 * @param timestamp ISO timestamp string
 * @param useTimeAgo Optional boolean to use relative time for recent timestamps (default: true)
 */
export function formatTimestamp(timestamp: string, useTimeAgo: boolean = true): string {
  if (useTimeAgo && isWithin24Hours(timestamp)) {
    return formatTimeAgo(timestamp);
  }
  return dayjs(timestamp).format('MMM D, YYYY');
}

/**
 * Gets consistent terminology for deadline formatting
 */
function getTerminology() {
  return {
    pastTense: 'Ended',
    presentTense: 'Ends',
    pastToday: 'Ended today',
    presentToday: 'Ends today',
    presentTomorrow: 'Ends tomorrow',
  };
}

/**
 * Formats countdown for same-day deadlines
 */
function formatSameDayCountdown(
  deadlineDate: dayjs.Dayjs,
  now: dayjs.Dayjs,
  terminology: ReturnType<typeof getTerminology>
): string {
  const diffMs = deadlineDate.diff(now);
  if (diffMs < 0) {
    return terminology.pastToday;
  }

  const diffHours = deadlineDate.diff(now, 'hour');
  const diffMinutes = deadlineDate.diff(now, 'minute');

  // Show countdown for same-day deadlines
  if (diffHours === 0) {
    return `${terminology.presentTense} in ${diffMinutes}m`;
  }

  return `${terminology.presentTense} in ${diffHours}h`;
}

/**
 * Formats a deadline timestamp into a human-readable countdown string
 *
 * This function provides consistent deadline formatting across the application
 * using unified "Ends" terminology for all deadline types.
 *
 * @param deadline ISO timestamp string of the deadline
 * @returns Formatted deadline string with countdown logic:
 *   - Under 1 hour: Shows minutes (e.g., "Ends in 30m")
 *   - 1-23 hours: Shows hours (e.g., "Ends in 3h")
 *   - 1 day: Shows "Ends tomorrow"
 *   - 2-29 days: Shows "X days left"
 *   - 30+ days: Shows full date
 *   - Past deadline: Shows "Ended"
 */
export function formatDeadline(deadline: string): string {
  const now = dayjs();
  const deadlineDate = dayjs(deadline);
  const diffDays = deadlineDate.diff(now, 'day');
  const terminology = getTerminology();

  // Handle past deadlines
  if (diffDays < 0) {
    return terminology.pastTense;
  }

  // Handle same-day deadlines
  if (diffDays === 0) {
    return formatSameDayCountdown(deadlineDate, now, terminology);
  }

  // Handle future deadlines
  if (diffDays === 1) {
    return terminology.presentTomorrow;
  }

  if (diffDays < 30) {
    return `${diffDays} days left`;
  }

  // Long-term deadlines: show full date
  return `${terminology.presentTense} ${formatTimestamp(deadline)}`;
}

/**
 * Checks if a deadline is in the future
 * @param deadline ISO timestamp string
 * @returns boolean indicating if the deadline is in the future
 */
export function isDeadlineInFuture(deadline: string): boolean {
  const now = dayjs();
  const deadlineDate = dayjs(deadline);
  return deadlineDate.isAfter(now);
}

export function specificTimeSince(dateInput: string | Date): string {
  const now = dayjs.utc();
  const joined = dayjs.utc(dateInput);

  // If the date is in the future, swap so we always count up
  const from = joined.isAfter(now) ? now : joined;
  const to = joined.isAfter(now) ? joined : now;

  let years = to.diff(from, 'year');
  let afterYears = from.add(years, 'year');

  let months = to.diff(afterYears, 'month');
  let afterMonths = afterYears.add(months, 'month');

  let days = to.diff(afterMonths, 'day');

  let result = [];
  if (years > 0) result.push(`${years} year${years > 1 ? 's' : ''}`);
  if (months > 0) result.push(`${months} month${months > 1 ? 's' : ''}`);
  if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);

  if (result.length === 0) {
    return 'just joined';
  } else if (result.length === 1) {
    return result[0];
  } else if (result.length === 2) {
    return `${result[0]} and ${result[1]}`;
  } else {
    return `${result[0]}, ${result[1]} and ${result[2]}`;
  }
}

/**
 * Formats a deadline timestamp into a human-readable string with exact time
 * @param deadline ISO timestamp string
 * @returns Formatted deadline string (e.g. "Dec 15, 2024 at 12:00 PM")
 */
export const formatExactTime = (deadline: string): string => {
  return dayjs(deadline).format('MMM D, YYYY [at] h:mm A');
};

/**
 * Formats a date timestamp into a human-readable string
 * @param datetime ISO timestamp string
 * @returns Formatted date string (e.g. "Dec 15, 2024")
 */
export const formatDate = (datetime: string, format: string = 'MMM D, YYYY'): string => {
  return dayjs(datetime).format(format);
};

/** Pluralizes a unit (e.g., "1 day" vs "2 days") */
const pluralize = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/**
 * Formats remaining time until a target date as a countdown string
 * @param targetDate ISO timestamp, or null
 * @returns { isPast: true } if past/null, or { isPast: false, formatted: "3 days 2 hours" }
 */
export function formatCountdownRemaining(targetDate: string | null): {
  isPast: boolean;
  formatted: string | null;
} {
  if (!targetDate) return { isPast: true, formatted: null };

  const diffMs = dayjs(targetDate).diff(dayjs());
  if (diffMs <= 0) return { isPast: true, formatted: null };

  const dur = dayjs.duration(diffMs);
  const days = Math.floor(dur.asDays());
  const hours = dur.hours();
  const minutes = dur.minutes();

  const parts = [
    days > 0 && pluralize(days, 'day'),
    hours > 0 && pluralize(hours, 'hour'),
    minutes > 0 && pluralize(minutes, 'minute'),
  ].filter(Boolean);

  return { isPast: false, formatted: parts.join(' ') || 'less than a minute' };
}

/**
 * Returns the number of remaining days until the target date, or null if past/missing.
 */
export function getRemainingDays(targetDate: string | null): number | null {
  if (!targetDate) return null;
  const diffMs = dayjs(targetDate).diff(dayjs());
  if (diffMs <= 0) return null;
  return dayjs.duration(diffMs).asDays();
}
